import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Button, Typography, Paper, Divider, Chip, Grid, Card, CardContent, 
  CardHeader, Avatar, Stepper, Step, StepLabel, Dialog, DialogTitle, 
  DialogContent, DialogActions, TextField, FormControl, InputLabel, Select,
  MenuItem, CircularProgress, IconButton, List, ListItem, ListItemText,
  ListItemSecondaryAction, Tooltip, InputAdornment
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Update as UpdateIcon,
  Print as PrintIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  SwapHoriz as SwapHorizIcon,
  MonetizationOn as MonetizationOnIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import apiService from '../../services/apiService';

const TransferDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [transfer, setTransfer] = useState(null);
  const [transferStates, setTransferStates] = useState([]);
  const [transferCommissions, setTransferCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estado para diálogo de cambio de estado
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusComments, setStatusComments] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  // Estado para diálogo de comisión personalizada
  const [openCommissionDialog, setOpenCommissionDialog] = useState(false);
  const [customCommissionValue, setCustomCommissionValue] = useState('');
  const [addingCommission, setAddingCommission] = useState(false);

  useEffect(() => {
    const fetchTransferData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Obtener datos de la transferencia
        try {
          const transferData = await apiService.getTransfer(id);
          console.log('Datos de transferencia recibidos:', transferData);
          setTransfer(transferData);
        } catch (err) {
          console.error('Error al cargar datos básicos de la transferencia:', err);
          setError('Error al cargar los datos básicos de la transferencia.');
          setTransfer(null);
          setLoading(false);
          return;
        }
        
        // Obtener estados de la transferencia
        try {
          const statesData = await apiService.getTransferStates(id);
          console.log('Estados de transferencia recibidos:', statesData);
          setTransferStates(statesData);
        } catch (err) {
          console.error('Error al cargar estados de la transferencia:', err);
          setTransferStates([]);
        }
        
        // Obtener comisiones
        try {
          const commissionsData = await apiService.getTransferCommissions(id);
          console.log('Comisiones de transferencia recibidas:', commissionsData);
          setTransferCommissions(commissionsData);
        } catch (err) {
          console.error('Error al cargar comisiones de la transferencia:', err);
          setTransferCommissions([]);
        }
        
      } catch (err) {
        console.error('Error al cargar datos de la transferencia:', err);
        setError('Error al cargar los datos de la transferencia. Por favor, intente nuevamente.');
      } finally {
        setLoading(false);
      }
    };

    fetchTransferData();
  }, [id]);

  const handleBack = () => {
    navigate('/transfers');
  };

  const handleOpenStatusDialog = () => {
    setOpenStatusDialog(true);
  };

  const handleCloseStatusDialog = () => {
    setOpenStatusDialog(false);
    setNewStatus('');
    setStatusComments('');
  };
  
  const handleOpenCommissionDialog = () => {
    // No permitir abrir el diálogo si la transferencia está finalizada
    if (transfer && transfer.status === 'finalizado') {
      return;
    }
    
    setOpenCommissionDialog(true);
    setCustomCommissionValue('');
  };
  
  const handleCloseCommissionDialog = () => {
    setOpenCommissionDialog(false);
    setCustomCommissionValue('');
  };

  const handleChangeStatus = async () => {
    if (!newStatus) return;
    
    try {
      setUpdatingStatus(true);
      await apiService.changeTransferStatus(id, newStatus, statusComments);
      
      // Recargar datos
      const transferData = await apiService.getTransfer(id);
      setTransfer(transferData);
      
      const statesData = await apiService.getTransferStates(id);
      setTransferStates(statesData);
      
      handleCloseStatusDialog();
    } catch (err) {
      console.error('Error al cambiar estado de la transferencia:', err);
      setError('Error al cambiar el estado de la transferencia. Por favor, intente nuevamente.');
    } finally {
      setUpdatingStatus(false);
    }
  };
  
  const handleAddCustomCommission = async () => {
    // Verificar estado antes de añadir comisión
    if (transfer && transfer.status === 'finalizado') {
      setError('No se pueden añadir comisiones a transferencias finalizadas');
      handleCloseCommissionDialog();
      return;
    }
    
    if (!customCommissionValue || isNaN(parseFloat(customCommissionValue)) || parseFloat(customCommissionValue) <= 0) {
      return;
    }
    
    try {
      setAddingCommission(true);
      await apiService.createCustomCommission(id, parseFloat(customCommissionValue));
      
      // Actualizar lista de comisiones
      const commissionsData = await apiService.getTransferCommissions(id);
      setTransferCommissions(commissionsData);
      
      handleCloseCommissionDialog();
    } catch (err) {
      console.error('Error al añadir comisión personalizada:', err);
      setError('Error al añadir la comisión personalizada. Por favor, intente nuevamente.');
    } finally {
      setAddingCommission(false);
    }
  };
  
  const handleDeleteCommission = async (commissionId) => {
    // Verificar estado antes de eliminar comisión
    if (transfer && transfer.status === 'finalizado') {
      setError('No se pueden eliminar comisiones de transferencias finalizadas');
      return;
    }
    
    try {
      await apiService.deleteTransferCommission(commissionId);
      
      // Actualizar lista de comisiones
      const commissionsData = await apiService.getTransferCommissions(id);
      setTransferCommissions(commissionsData);
    } catch (err) {
      console.error(`Error al eliminar comisión ${commissionId}:`, err);
      setError('Error al eliminar la comisión. Por favor, intente nuevamente.');
    }
  };

  const getStatusChip = (status) => {
    let color;
    let label;
    
    switch (status) {
      case 'pending':
        color = 'warning';
        label = 'Pendiente';
        break;
      case 'processed':
        color = 'info';
        label = 'Procesada';
        break;
      case 'canceled':
        color = 'default';
        label = 'Cancelada';
        break;
      case 'finished':
        color = 'success';
        label = 'Finalizada';
        break;
      default:
        color = 'default';
        label = status;
    }
    
    return <Chip label={label} color={color} />;
  };

  const renderStatus = (status) => {
    switch (status) {
      case 'pending':
        return 'Pendiente';
      case 'processed':
        return 'Procesada';
      case 'canceled':
        return 'Cancelada';
      case 'finished':
        return 'Finalizada';
      default:
        return status;
    }
  };

  const formatCurrency = (amount, currency) => {
    const formatter = new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: currency === 'PEN' ? 'PEN' : 'USD',
      minimumFractionDigits: 2
    });
    
    return formatter.format(amount);
  };

  const getStatusSteps = () => {
    const steps = [
      { status: 'pending', label: 'Pendiente', icon: <UpdateIcon /> },
      { status: 'processed', label: 'Procesada', icon: <ReceiptIcon /> },
      { status: 'finished', label: 'Finalizada', icon: <CheckCircleIcon /> }
    ];
    
    // Si está cancelada, mostrar un paso especial
    if (transfer.status === 'canceled') {
      return [
        { status: 'pending', label: 'Pendiente', icon: <UpdateIcon /> },
        { status: 'canceled', label: 'Cancelada', icon: <CancelIcon /> }
      ];
    }
    
    return steps;
  };

  const getActiveStep = () => {
    const statusMap = {
      'pending': 0,
      'processed': 1,
      'finished': 2,
      'canceled': 1 // En caso de cancelación, el paso activo es 1
    };
    
    return statusMap[transfer.status] || 0;
  };
  
  // Calcular el total con todas las comisiones
  const getTotalAmount = () => {
    if (!transfer) return 0;
    
    const commissionTotal = transferCommissions.reduce(
      (total, comm) => total + parseFloat(comm.applied_commission), 
      0
    );
    
    return parseFloat(transfer.amount) + commissionTotal;
  };

  const handlePrint = () => {
    // Abrir ventana de tamaño normal
    const printWindow = window.open('', '_blank');
    
    if (!printWindow) {
      alert('Por favor, permite las ventanas emergentes para imprimir el comprobante.');
      return;
    }
    
    // Generar el HTML para el recibo
    const transferDate = new Date(transfer.register_date).toLocaleString();
    const commissionTotal = transferCommissions.reduce(
      (total, comm) => total + parseFloat(comm.applied_commission), 
      0
    );
    
    // Extraer nombres de los objetos anidados
    const clientOriginName = transfer.client_origin ? 
      `${transfer.client_origin.first_name} ${transfer.client_origin.father_last_name}` : 
      'No especificado';
    
    const clientDestinyName = transfer.client_destiny ? 
      `${transfer.client_destiny.first_name} ${transfer.client_destiny.father_last_name}` : 
      'No especificado';
    
    const branchOriginName = transfer.branch_origin ? 
      transfer.branch_origin.branch_name : 
      'No especificada';
    
    const branchDestinyName = transfer.branch_destiny ? 
      transfer.branch_destiny.branch_name : 
      'No especificada';
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Comprobante de Transferencia #${transfer.id}</title>
        <meta charset="UTF-8">
        <style>
          * {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
          }
          
          html, body {
            width: 57mm;
            height: 58mm;
            overflow: hidden;
            font-family: Arial, sans-serif;
            font-size: 9px;
            line-height: 1.2;
          }
          
          @media print {
            @page {
              size: 57mm 58mm;
              margin: 0;
            }
            
            html, body {
              width: 57mm;
              height: 58mm;
            }
            
            .page-break {
              display: none;
            }
          }
          
          .ticket-container {
            width: 100%;
            height: 100%;
            padding: 3mm;
            display: flex;
            flex-direction: column;
          }
          
          .ticket-header {
            text-align: center;
            border-bottom: 1px dashed #000;
            padding-bottom: 2px;
            margin-bottom: 2px;
          }
          
          .ticket-header h1 {
            font-size: 11px;
            margin-bottom: 1px;
          }
          
          .ticket-header p {
            font-size: 8px;
            line-height: 1.1;
          }
          
          .ticket-content {
            flex: 1;
          }
          
          .ticket-row {
            display: flex;
            justify-content: space-between;
            margin: 1px 0;
          }
          
          .ticket-row .label {
            font-weight: bold;
          }
          
          .ticket-footer {
            text-align: center;
            font-size: 7px;
            border-top: 1px dashed #000;
            padding-top: 2px;
            margin-top: auto;
          }
        </style>
      </head>
      <body>
        <div class="ticket-container">
          <div class="ticket-header">
            <h1>GIROS RÁPIDOS</h1>
            <p>Comprobante de Transferencia</p>
            <p>Fecha: ${transferDate}</p>
          </div>
          
          <div class="ticket-content">
            <div class="ticket-row">
              <span class="label">Nº Transferencia:</span>
              <span>#${transfer.id}</span>
            </div>
            <div class="ticket-row">
              <span class="label">Estado:</span>
              <span>${renderStatus(transfer.status)}</span>
            </div>
            <div class="ticket-row">
              <span class="label">De:</span>
              <span>${clientOriginName}</span>
            </div>
            <div class="ticket-row">
              <span class="label">Para:</span>
              <span>${clientDestinyName}</span>
            </div>
            <div class="ticket-row">
              <span class="label">Origen:</span>
              <span>${branchOriginName}</span>
            </div>
            <div class="ticket-row">
              <span class="label">Destino:</span>
              <span>${branchDestinyName}</span>
            </div>
            <div class="ticket-row">
              <span class="label">Monto:</span>
              <span>${formatCurrency(transfer.amount, transfer.currency_type)}</span>
            </div>
            
            ${transferCommissions.length > 0 ? `
              <div class="ticket-row">
                <span class="label">Comisiones:</span>
                <span>${formatCurrency(commissionTotal, transfer.currency_type)}</span>
              </div>
              <div class="ticket-row">
                <span class="label">Total:</span>
                <span>${formatCurrency(parseFloat(transfer.amount) + commissionTotal, transfer.currency_type)}</span>
              </div>
            ` : ''}
          </div>
          
          <div class="ticket-footer">
            <p>Gracias por su preferencia</p>
            <p>Este documento no es un comprobante fiscal</p>
          </div>
        </div>
        
        <script>
          window.onload = function() {
            window.print();
          };
        </script>
      </body>
      </html>
    `;
    
    // Escribir el contenido en la ventana emergente
    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !transfer) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Typography color="error">{error || 'Transferencia no encontrada'}</Typography>
      </Box>
    );
  }

  const steps = getStatusSteps();
  const activeStep = getActiveStep();

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            Volver
          </Button>
          <Typography variant="h4">
            Transferencia #{transfer.id}
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{ mr: 2 }}
          >
            Imprimir
          </Button>
          {transfer.status !== 'canceled' && transfer.status !== 'finalizado' && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<UpdateIcon />}
              onClick={handleOpenStatusDialog}
              sx={{ mr: 1 }}
            >
              Cambiar Estado
            </Button>
          )}
          {transfer.status !== 'finalizado' && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<MonetizationOnIcon />}
              onClick={handleOpenCommissionDialog}
            >
              Añadir Comisión
            </Button>
          )}
        </Box>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main', mr: 2 }}>
            <SwapHorizIcon fontSize="large" />
          </Avatar>
          <Box>
            <Typography variant="h5">
              Transferencia de {formatCurrency(transfer.amount, transfer.currency_type)}
            </Typography>
            <Box display="flex" alignItems="center" mt={0.5}>
              {getStatusChip(transfer.status)}
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                Registrada: {new Date(transfer.register_date).toLocaleString()}
              </Typography>
            </Box>
          </Box>
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((step) => (
            <Step key={step.status}>
              <StepLabel
                StepIconProps={{
                  icon: step.icon
                }}
              >
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
        
        <Divider sx={{ my: 2 }} />
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader 
                title="Sucursal Origen" 
                avatar={<Avatar><BusinessIcon /></Avatar>}
              />
              <Divider />
              <CardContent>
                {transfer.branch_origin ? (
                  <>
                    <Typography variant="h6">
                      {transfer.branch_origin.branch_name || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {transfer.branch_origin.district_name ? 
                        `${transfer.branch_origin.district_name}, ${transfer.branch_origin.province_name}, ${transfer.branch_origin.department_name}` : 
                        'Sin ubicación registrada'}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    Información de la sucursal no disponible
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader 
                title="Sucursal Destino" 
                avatar={<Avatar><BusinessIcon /></Avatar>}
              />
              <Divider />
              <CardContent>
                {transfer.branch_destiny ? (
                  <>
                    <Typography variant="h6">
                      {transfer.branch_destiny.branch_name || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {transfer.branch_destiny.district_name ? 
                        `${transfer.branch_destiny.district_name}, ${transfer.branch_destiny.province_name}, ${transfer.branch_destiny.department_name}` : 
                        'Sin ubicación registrada'}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    Información de la sucursal no disponible
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12}>
            <Card>
              <CardHeader 
                title="Información Adicional" 
                avatar={<Avatar><PersonIcon /></Avatar>}
              />
              <Divider />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Portavalor / Responsable:
                    </Typography>
                    <Typography variant="body1">
                      {transfer.user ? 
                        `${transfer.user.first_name || ''} ${transfer.user.last_name || ''}` : 
                        'No especificado'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Fecha de Registro:
                    </Typography>
                    <Typography variant="body1">
                      {new Date(transfer.register_date).toLocaleString()}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader 
                title="Cliente Origen" 
                avatar={<Avatar><PersonIcon /></Avatar>}
              />
              <Divider />
              <CardContent>
                {transfer.client_origin ? (
                  <>
                    <Typography variant="h6">
                      {`${transfer.client_origin.first_name || 'N/A'} ${transfer.client_origin.father_last_name || ''} ${transfer.client_origin.mother_last_name || ''}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {`${transfer.client_origin.document_type || 'Doc'}: ${transfer.client_origin.document_number || 'N/A'}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {`Teléfono: ${transfer.client_origin.phone_prefix || ''} ${transfer.client_origin.phone_number || 'N/A'}`}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    Información del cliente no disponible
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader 
                title="Cliente Destino" 
                avatar={<Avatar><PersonIcon /></Avatar>}
              />
              <Divider />
              <CardContent>
                {transfer.client_destiny ? (
                  <>
                    <Typography variant="h6">
                      {`${transfer.client_destiny.first_name || 'N/A'} ${transfer.client_destiny.father_last_name || ''} ${transfer.client_destiny.mother_last_name || ''}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {`${transfer.client_destiny.document_type || 'Doc'}: ${transfer.client_destiny.document_number || 'N/A'}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {`Teléfono: ${transfer.client_destiny.phone_prefix || ''} ${transfer.client_destiny.phone_number || 'N/A'}`}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    Información del cliente no disponible
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12}>
            <Card>
              <CardHeader 
                title="Comisiones" 
                avatar={<Avatar><MonetizationOnIcon /></Avatar>}
              />
              <Divider />
              <CardContent>
                {transferCommissions.length > 0 ? (
                  <>
                    <List>
                      {transferCommissions.map((comm) => (
                        <ListItem key={comm.id} divider>
                          <ListItemText
                            primary={formatCurrency(comm.applied_commission, transfer.currency_type)}
                            secondary={`ID Comisión: ${comm.commission}`}
                          />
                          <ListItemSecondaryAction>
                            {transfer.status !== 'finalizado' && (
                              <Tooltip title="Eliminar comisión">
                                <IconButton 
                                  edge="end" 
                                  aria-label="delete"
                                  onClick={() => handleDeleteCommission(comm.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                    <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle1">
                        Subtotal: {formatCurrency(transfer.amount, transfer.currency_type)}
                      </Typography>
                      <Typography variant="subtitle1">
                        Total con comisiones: {formatCurrency(getTotalAmount(), transfer.currency_type)}
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <Box textAlign="center" py={2}>
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                      No hay comisiones aplicadas a esta transferencia
                    </Typography>
                    {transfer.status !== 'finalizado' ? (
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleOpenCommissionDialog}
                        sx={{ mt: 1 }}
                      >
                        Añadir Comisión
                      </Button>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No se pueden añadir comisiones a transferencias finalizadas
                      </Typography>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Comentarios" />
              <Divider />
              <CardContent>
                <Typography variant="body1">
                  {transfer.comments || 'Sin comentarios'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Historial de Estados" />
              <Divider />
              <CardContent>
                {transferStates.length > 0 ? (
                  <List>
                    {transferStates.map((state) => (
                      <ListItem key={state.id} divider>
                        <ListItemText
                          primary={getStatusChip(state.status)}
                          secondary={
                            <>
                              <Typography variant="body2" component="span">
                                {new Date(state.changed_at).toLocaleString()}
                              </Typography>
                              {state.comments && (
                                <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 1 }}>
                                  {state.comments}
                                </Typography>
                              )}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    No hay historial de estados disponible
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Diálogo para cambiar estado */}
      <Dialog open={openStatusDialog} onClose={handleCloseStatusDialog}>
        <DialogTitle>Cambiar Estado de la Transferencia</DialogTitle>
        <DialogContent>
          <Box py={1}>
            <FormControl fullWidth margin="normal">
              <InputLabel id="status-label">Nuevo Estado</InputLabel>
              <Select
                labelId="status-label"
                value={newStatus}
                label="Nuevo Estado"
                onChange={(e) => setNewStatus(e.target.value)}
              >
                <MenuItem value="">Seleccionar</MenuItem>
                {transfer.status === 'pending' && (
                  <>
                    <MenuItem value="processed">Procesada</MenuItem>
                    <MenuItem value="canceled">Cancelada</MenuItem>
                  </>
                )}
                {transfer.status === 'processed' && (
                  <>
                    <MenuItem value="finished">Finalizada</MenuItem>
                    <MenuItem value="canceled">Cancelada</MenuItem>
                  </>
                )}
              </Select>
            </FormControl>
            
            <TextField
              margin="normal"
              fullWidth
              label="Comentarios"
              multiline
              rows={3}
              value={statusComments}
              onChange={(e) => setStatusComments(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStatusDialog}>Cancelar</Button>
          <Button 
            onClick={handleChangeStatus} 
            color="primary" 
            disabled={!newStatus || updatingStatus}
            startIcon={updatingStatus ? <CircularProgress size={20} /> : null}
          >
            {updatingStatus ? 'Actualizando...' : 'Actualizar'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Diálogo para añadir comisión personalizada */}
      <Dialog open={openCommissionDialog} onClose={handleCloseCommissionDialog}>
        <DialogTitle>Agregar Comisión Personalizada</DialogTitle>
        <DialogContent>
          <Box py={1}>
            <TextField
              autoFocus
              margin="dense"
              label="Valor de Comisión"
              type="number"
              fullWidth
              value={customCommissionValue}
              onChange={(e) => setCustomCommissionValue(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {transfer?.currency_type === 'PEN' ? 'S/.' : '$'}
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCommissionDialog}>Cancelar</Button>
          <Button 
            onClick={handleAddCustomCommission} 
            color="primary"
            disabled={addingCommission || !customCommissionValue}
          >
            {addingCommission ? 'Agregando...' : 'Agregar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TransferDetail;